import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {invalidateOpenClosedCounts} from '@github-ui/issues-tab-counter/open-closed-count'
import {ListView} from '@github-ui/list-view'
import {IssueRow, IssuesIndexSecondaryGraphqlQuery} from '@github-ui/list-view-items-issues-prs/IssueRow'
import type {IssueRowSecondaryQuery} from '@github-ui/list-view-items-issues-prs/IssuesIndexSecondaryQuery'
import {NoResults} from '@github-ui/list-view-items-issues-prs/NoResults'
import {PullRequestRow} from '@github-ui/list-view-items-issues-prs/PullRequestRow'
import {checkIfQuerySupportsPr, isPrsOnly, parseQuery} from '@github-ui/list-view-items-issues-prs/Query'
import {
  getSelectedSortOptionKeyFromQuery,
  handleEmojiMapValue,
} from '@github-ui/list-view-items-issues-prs/SortingDropdown'
import {noop} from '@github-ui/noop'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import type {NavigateOptions, To} from '@github-ui/react-router'
import {EMOJI_MAP} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import type {ReportableError} from '@github-ui/relay-preloaded-query-boundary'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {useCacheQueryLoader} from '@github-ui/relay-query-cache/hooks/use-cache-query-loader'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {testIdProps} from '@github-ui/test-id-props'
import {GlobalCommands, ScopedCommands} from '@github-ui/ui-commands'
import {type AnalyticsEvent, useAnalytics} from '@github-ui/use-analytics'
import {useIsPlatform} from '@github-ui/use-is-platform'
import {useNavigate} from '@github-ui/use-navigate'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useUser} from '@github-ui/use-user'
import {Pagination} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type MouseEvent, type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, usePaginationFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {VALUES} from '../../constants/values'
import {useActiveSearchQuery, useCurrentPage, useQueryBulk, useSetCurrentPage} from '../../contexts/QueryContext'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import type {AppPayload} from '../../types/app-payload'
import type {IssueSearchType} from '../../types/issue-search-type'
import {getPageNumberFromUrlQuery, replacePageNumberInUrl} from '../../utils/urls'
import type {
  ListItemsPaginated_results$data,
  ListItemsPaginated_results$key,
} from './__generated__/ListItemsPaginated_results.graphql'
import type {SearchPaginatedQuery} from './__generated__/SearchPaginatedQuery.graphql'
import {ListItemsHeader} from './header/ListItemsHeader'
import {useShiftKey} from './hooks/use-shift-key'
import styles from './ListItems.module.css'
import {MoreResultsAvailableBanner} from './MoreResultsAvailableBanner'

const NOOP_FOCUS_COMMANDS = {'issues-react:focus-next-issue': noop, 'issues-react:focus-previous-issue': noop} as const

type ListProps = {
  search: ListItemsPaginated_results$key
  queryFromCustomView?: string | null
  listRef: MutableRefObject<HTMLUListElement | undefined>
  isBulkSupported: boolean
  isInOrganization?: boolean
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  onIssueNavigate?: (position?: number) => void
  updateIssueSearchType?: (issueSearchType: string | null | undefined) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
  searchType?: IssueSearchType
}

type PaginationQueryProps = {
  key: ListItemsPaginated_results$key
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ListItemsFragment = graphql`
  fragment ListItemsPaginated_results on Searchable
  @argumentDefinitions(
    cursor: {type: "String", defaultValue: null}
    query: {type: "String!"}
    first: {type: "Int"}
    labelPageSize: {type: "Int!"}
    skip: {type: "Int", defaultValue: null}
    fetchRepository: {type: "Boolean!"}
    type: {type: "SearchType", defaultValue: ISSUE_ADVANCED}
  )
  @refetchable(queryName: "SearchPaginatedQuery") {
    search(first: $first, after: $cursor, query: $query, type: $type, skip: $skip)
      @catch(to: RESULT)
      @stream_connection(key: "Query_search", initial_count: 12) {
      edges @required(action: THROW) {
        node {
          ... on Issue {
            id
            __typename
            number
            repository {
              name
              owner {
                login
              }
            }
            ...IssueRow
              @dangerously_unaliased_fixme
              @arguments(labelPageSize: $labelPageSize, fetchRepository: $fetchRepository)
            # eslint-disable-next-line relay/must-colocate-fragment-spreads
            ...ApplyAssigneesBulkActionIssueFragment @dangerously_unaliased_fixme
          }
          ... on PullRequest {
            id
            __typename
            number
            repository {
              name
              owner {
                login
              }
            }
            ...PullRequestRow @dangerously_unaliased_fixme @arguments(labelPageSize: $labelPageSize)
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
      issueCount
      issueSearchType
    }
  }
`

function SearchFunction({key}: PaginationQueryProps) {
  const {data, refetch} = usePaginationFragment<SearchPaginatedQuery, ListItemsPaginated_results$key>(
    ListItemsFragment,
    key,
  )

  return {data, refetch}
}

// ---------------------------------------------------------------------------
// RowItem — memo'd wrapper so IssueRow/PullRequestRow React.memo is effective.
//
// The callbacks (onSelect, onNavigate) reference state that changes frequently
// (checkedItems, handleNavigate). Passing them as plain function props would
// create new references on every ListItems render, defeating the memo wrapping
// on IssueRow and PullRequestRow. Using MutableRefs lets us update the live
// function without changing the ref object identity, so RowItem (and its
// memo'd children) only re-render when props that truly affect the UI change.
// ---------------------------------------------------------------------------

type RowItemProps = {
  node: IssueNodeType | PullRequestNodeType
  index: number
  isSelected: boolean
  metadataRef: Parameters<typeof IssueRow>[0]['metadataRef']
  onSelectRow: Parameters<typeof IssueRow>[0]['onSelectRow']
  getMetadataHref: Parameters<typeof IssueRow>[0]['getMetadataHref']
  getAdditiveMetadataHref: Parameters<typeof IssueRow>[0]['getAdditiveMetadataHref']
  onSidePanelNavigate?: Parameters<typeof IssueRow>[0]['onSidePanelNavigate']
  reactionEmojiToDisplay: Parameters<typeof IssueRow>[0]['reactionEmojiToDisplay']
  sortingItemSelected: string
  scopedRepository: Parameters<typeof IssueRow>[0]['scopedRepository']
  navigate: (to: To) => void
  itemSelectedRef: MutableRefObject<(id: string, node: ItemNodeType, selected: boolean) => void>
  handleNavigateRef: MutableRefObject<(to: To, options?: NavigateOptions, position?: number) => void>
}

function RowItemComponent({
  node,
  index,
  isSelected,
  metadataRef,
  onSelectRow,
  getMetadataHref,
  getAdditiveMetadataHref,
  onSidePanelNavigate,
  reactionEmojiToDisplay,
  sortingItemSelected,
  scopedRepository,
  navigate,
  itemSelectedRef,
  handleNavigateRef,
}: RowItemProps) {
  const onSelect = useCallback(
    (selected: boolean) => itemSelectedRef.current(node.id, node, selected),
    [node, itemSelectedRef],
  )

  const onNavigateIssue = useCallback(
    (to: To, options?: NavigateOptions) => handleNavigateRef.current(to, options, index),
    [handleNavigateRef, index],
  )

  return (
    <div className={styles.listItem}>
      {node.__typename === 'Issue' && (
        <IssueRow
          issueKey={node}
          metadataRef={metadataRef}
          isActive={false}
          isSelected={isSelected}
          getMetadataHref={getMetadataHref}
          onSelect={onSelect}
          onSelectRow={onSelectRow}
          onNavigate={onNavigateIssue}
          onSidePanelNavigate={onSidePanelNavigate}
          reactionEmojiToDisplay={reactionEmojiToDisplay}
          sortingItemSelected={sortingItemSelected}
          scopedRepository={scopedRepository}
          data-testid={TEST_IDS.issueRowItem(node.number)}
          getAdditiveMetadataHref={getAdditiveMetadataHref}
        />
      )}
      {node.__typename === 'PullRequest' && (
        <PullRequestRow
          pullRequestKey={node}
          metadataRef={metadataRef}
          onNavigate={navigate}
          isActive={false}
          isSelected={isSelected}
          getMetadataHref={getMetadataHref}
          onSelect={onSelect}
          onSelectRow={onSelectRow}
          reactionEmojiToDisplay={reactionEmojiToDisplay}
          sortingItemSelected={sortingItemSelected}
          data-testid={TEST_IDS.pullRequestRowItem(node.number)}
        />
      )}
    </div>
  )
}

const RowItem = memo(RowItemComponent)

/**
 * See ListView stories for a representation of this component.
 * packages/list-view/src/stories/RecentActivity/RecentActivity.stories.tsx
 * https://ui.githubapp.com/storybook/?path=/story/recipes-list-view-dotcom-pages--recent-activity
 * https://ui.githubapp.com/storybook/?path=/story/recipes-list-view-dotcom-pages--repository-issues
 */

/* A list of issues or pull requests that can be rendered in the sidebar or in the main content area.
 * @param {ItemIdentifier} itemIdentifier - Identifier for the current item displayed in the viewer (when the list is
      present in the sidebar). Undefined when the viewer is closed
 * @param {string} queryFromCustomView - The query corresponding to the custom view (if any)
 */
export function ListItems({
  search,
  queryFromCustomView,
  listRef,
  isBulkSupported,
  isInOrganization,
  onSidePanelNavigate,
  onIssueNavigate,
  updateIssueSearchType,
  onListItemsChange,
  searchType,
}: ListProps) {
  const path = `${ssrSafeLocation.pathname}${ssrSafeLocation.search}`

  const {scoped_repository, best_match_sort_enabled} = useAppPayload<AppPayload>()
  const {currentUser} = useUser()
  const activeSearchQuery = useActiveSearchQuery()
  const currentPage = useCurrentPage()
  const setCurrentPage = useSetCurrentPage()
  const [fromPagination, setFromPagination] = useState(false)
  const navigate = useNavigate()
  const isMac = useIsPlatform(['mac'])

  const [lastSelectedItem, setLastSelectedItem] = useState<{id: string; node: ItemNodeType}>()
  const {shiftKeyPressedRef} = useShiftKey()

  const [issueIndexLazyDataRef, loadIssueIndexLazyData] = useCacheQueryLoader<IssueRowSecondaryQuery>(
    IssuesIndexSecondaryGraphqlQuery,
    null,
    DEFAULT_RELAY_CACHE_TTL,
  )

  const {data: pageData, refetch} = SearchFunction({key: search})
  const searchDataMissing = pageData.search === null || pageData.search === undefined

  const errors = pageData.search?.ok ? null : pageData.search?.errors

  if (!pageData.search.ok) {
    const isServiceUnavailable = errors?.some(
      e => e && typeof e === 'object' && 'type' in e && e.type === 'SERVICE_UNAVAILABLE',
    )

    const errorMessage = `ListItemsPaginated: pageData.search.value.edges is errored. Errors: ${JSON.stringify(errors)}`
    const error: ReportableError = new Error(errorMessage)
    error.shouldSkipReport = isServiceUnavailable ?? false
    throw error
  }

  const {ok, value: {edges, issueCount, issueSearchType} = {}} = pageData.search
  const hasValidEdges = edges !== null && edges !== undefined
  const searchResultsReady = !searchDataMissing && ok && hasValidEdges

  // just to make sure we don't refetch the data more than once
  const [refetched, setRefetched] = useState(false)

  useEffect(() => {
    announce(LABELS.numberOfResults(issueCount || 0), {assertive: true})
  }, [issueCount])

  useEffect(() => {
    updateIssueSearchType?.(issueSearchType)
  }, [issueSearchType, updateIssueSearchType])

  // Workaround until we find a way to fix an issue where the data is null when we have the data in the browser cache
  // but it's marked as stale by Relay. See https://github.com/github/issues/issues/13005
  useEffect(() => {
    const hasValidParentNode = pageData.id && pageData.id !== 'RootQueryObject'
    const shouldRefetch = !refetched && searchDataMissing && hasValidParentNode
    if (!shouldRefetch) return

    const disposable = refetch({}, {fetchPolicy: 'network-only', onComplete: () => setRefetched(true)})

    // cleanup
    return disposable.dispose
  }, [activeSearchQuery, refetch, refetched, searchDataMissing, pageData])

  const data = useMemo(
    () =>
      edges
        ? edges
            .map(edge =>
              edge?.node && (edge.node.__typename === 'PullRequest' || edge.node.__typename === 'Issue')
                ? edge.node
                : null,
            )
            .filter(node => node != null)
        : [],
    [edges],
  )

  // Register fetchQuery as the bulk job success callback so the list re-queries on completion
  const {registerBulkJobSuccess} = useQueryBulk()
  const environment = useRelayEnvironment()

  useEffect(() => {
    if (!onListItemsChange) return
    const navigableItems: ItemIdentifier[] = data
      .map(node => {
        if (!node || !node.repository) return null
        if (node.__typename !== 'Issue' && node.__typename !== 'PullRequest') return null
        return {
          number: node.number,
          owner: node.repository.owner.login,
          repo: node.repository.name,
          type: node.__typename,
        }
      })
      .filter((item): item is ItemIdentifier => item !== null)
    onListItemsChange(navigableItems)
  }, [data, onListItemsChange])

  const paginationLoadingRef = useRef<HTMLDivElement>(null)

  const onlyPrs = useMemo(() => {
    return isPrsOnly(activeSearchQuery)
  }, [activeSearchQuery])

  const maxItems = VALUES.maxIssuesListItems(onlyPrs, !!currentUser)

  const totalPages = useMemo(() => {
    if (!issueCount) {
      return 0
    }

    const cappedIssueCount = Math.min(maxItems, issueCount)
    return Math.ceil(cappedIssueCount / VALUES.issuesPageSize())
  }, [issueCount, maxItems])

  const {getQueryFieldUrl, getAdditiveQueryFieldUrl, navigateToUrl} = useAppNavigate()

  const handlePageChange = useCallback(
    (e: MouseEvent, page_number: number) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isMetaKey = isMac ? e.metaKey : e.ctrlKey
      if (isMetaKey || e.shiftKey) {
        return
      }
      e.preventDefault()

      const updatedUrl = replacePageNumberInUrl(path, page_number)
      setCurrentPage(page_number)
      navigateToUrl(updatedUrl)
    },
    [isMac, path, setCurrentPage, navigateToUrl],
  )

  const buildPageHref = useCallback(
    (page_number: number) => {
      return replacePageNumberInUrl(path, page_number)
    },
    [path],
  )

  const {initialSortingItem, initialReactionEmojiToDisplay} = useMemo(() => {
    const parsedSortQuery = parseQuery(queryFromCustomView || activeSearchQuery).get('sort')

    const selectedKey = getSelectedSortOptionKeyFromQuery(parsedSortQuery?.[0] || '', best_match_sort_enabled)

    const reactionEmojiText = handleEmojiMapValue(selectedKey)
    const reactionEmoji = EMOJI_MAP[reactionEmojiText || '']

    return {
      initialSortingItem: selectedKey || 'created',
      initialReactionEmojiToDisplay: reactionEmoji ? {reaction: reactionEmojiText || '', reactionEmoji} : undefined,
    }
  }, [activeSearchQuery, queryFromCustomView, best_match_sort_enabled])

  const [reactionEmojiToDisplay, setReactionEmojiToDisplay] = useState(initialReactionEmojiToDisplay)
  const [sortingItemSelected, setSortingItemSelected] = useState<string>(initialSortingItem)
  const [listHasPRs, setListHasPRs] = useState(false)

  const {sendAnalyticsEvent} = useAnalytics()

  const useBulkActions = useMemo(() => {
    return isBulkSupported && !checkIfQuerySupportsPr(activeSearchQuery)
  }, [activeSearchQuery, isBulkSupported])

  const [checkedItems, setCheckedItems] = useState(() => new Map<string, ItemNodeType>())

  const bulkRefetchEnabled = isFeatureEnabled('issues_bulk_sync_search_indexing')

  useEffect(() => {
    if (!bulkRefetchEnabled) return

    registerBulkJobSuccess(() => {
      refetch({}, {fetchPolicy: 'store-and-network'})
      environment.commitUpdate(store => {
        invalidateOpenClosedCounts(store)
      })
    })
    return () => {
      registerBulkJobSuccess(null)
    }
  }, [refetch, registerBulkJobSuccess, bulkRefetchEnabled, environment])

  const updateListHasPRs = (checkedMap: Map<string, ItemNodeType>) => {
    const hasAnyPR = Array.from(checkedMap.values()).some(item => item.__typename === 'PullRequest')
    setListHasPRs(hasAnyPR)
  }

  const applyShiftSelection = useCallback(
    (newCheckedItems: Map<string, ItemNodeType>, fromId: string, toId: string, selected: boolean) => {
      const fromIndex = data.findIndex(d => d.id === fromId)
      const toIndex = data.findIndex(d => d.id === toId)
      if (fromIndex === -1 || toIndex === -1) return // handle cases where we've removed issues from the list
      for (let i = Math.min(fromIndex, toIndex); i <= Math.max(fromIndex, toIndex); i++) {
        const d = data[i] as ItemNodeType
        if (selected) {
          newCheckedItems.set(d.id, d)
        } else {
          newCheckedItems.delete(d.id)
        }
        updateListHasPRs(newCheckedItems)
      }
    },
    [data],
  )

  const itemSelected = useCallback(
    (id: string, node: ItemNodeType, selected: boolean) => {
      const newCheckedItems = new Map<string, ItemNodeType>(checkedItems)

      if (shiftKeyPressedRef.current && lastSelectedItem) {
        applyShiftSelection(newCheckedItems, lastSelectedItem.id, id, selected)
      } else {
        if (selected) {
          newCheckedItems.set(id, node)
        } else {
          newCheckedItems.delete(id)
        }
        updateListHasPRs(newCheckedItems)
      }

      setCheckedItems(newCheckedItems)
      setLastSelectedItem({id, node})
    },
    [applyShiftSelection, checkedItems, lastSelectedItem, shiftKeyPressedRef],
  )

  useEffect(() => {
    // If the underlying data changes, sync the data in `checkedItems` so child components that
    // read inline from those items have updated data.
    const anyMismatches = Array.from(checkedItems.values()).find(item => !data.find(node => node === item))
    if (anyMismatches) {
      setCheckedItems(
        data.reduce((map, item) => {
          if (item && checkedItems.has(item.id)) {
            map.set(item.id, item)
          }
          return map
        }, new Map<string, ItemNodeType>()),
      )
    }
  }, [checkedItems, data])

  useEffect(() => {
    const pageNumber = getPageNumberFromUrlQuery(ssrSafeLocation.search)
    setCurrentPage(pageNumber > 1 ? pageNumber : 1)
    setCheckedItems(new Map<string, ItemNodeType>())
  }, [setCurrentPage, path])

  // Stable array reference — only recomputed when `data` changes, not on every render.
  const issueNodes = useMemo(
    () =>
      data.reduce((arr, node) => {
        if (node != null) arr.push(node)
        return arr
      }, new Array<ItemNodeType>()),
    [data],
  )

  const listItemsHeader = (
    <ListItemsHeader
      checkedItems={checkedItems}
      issueCount={issueCount || 0}
      issueNodes={issueNodes}
      sortingItemSelected={sortingItemSelected}
      setCheckedItems={setCheckedItems}
      setReactionEmojiToDisplay={setReactionEmojiToDisplay}
      setSortingItemSelected={setSortingItemSelected}
      useBulkActions={useBulkActions}
      setCurrentPage={setCurrentPage}
      listHasPRs={listHasPRs}
      updateListHasPRs={updateListHasPRs}
      isInOrganization={isInOrganization}
      searchType={searchType}
    />
  )

  const onSelectRow = useCallback(
    (payload?: {[key: string]: unknown} | AnalyticsEvent | undefined) => {
      sendAnalyticsEvent('search_results.select_row', 'SEARCH_RESULT_ROW', {...payload})
    },
    [sendAnalyticsEvent],
  )

  const currentPageNodes = data

  const currentPageNodeTypes = useMemo(
    () => new Set(currentPageNodes?.map(node => node?.__typename) || []),
    [currentPageNodes],
  )
  const handleNavigate = useCallback(
    (to: To, options = {}, position?: number) => {
      onIssueNavigate?.(position)
      return navigateToUrl(to, options, true)
    },
    [navigateToUrl, onIssueNavigate],
  )

  // Refs keep the callback identities stable so RowItem (and its memo'd children)
  // don't re-render just because checkedItems or handleNavigate changed identity.
  const itemSelectedRef = useRef(itemSelected)
  itemSelectedRef.current = itemSelected

  const handleNavigateRef = useRef(handleNavigate)
  handleNavigateRef.current = handleNavigate

  const [deletedRecordId] = useSessionStorage<string>(
    `${scoped_repository?.owner}-${scoped_repository?.name}-deletedRecordId`,
    '',
  )
  const nodes = useMemo(
    () =>
      data
        .map(node => node?.id)
        .filter(id => id !== deletedRecordId)
        .filter(Boolean),
    [data, deletedRecordId],
  )

  const hasLazyData = issueIndexLazyDataRef !== null
  useEffect(() => {
    // The app currently fires loadIssueIndexLazyData twice on paginations.
    // The following prevents that.  Unfortunately, this introduced a bug where the secondary query wasn't being fired at all for other navigation types
    // For now we're commenting it out to fix the regression, but a followup item is needed to eliminate the duplicate queries during paging.
    // if (hasLazyData) {
    //   // This fixes an issue where the secondary query was being fired twice in certain navigations
    //   return
    // }
    loadIssueIndexLazyData({nodes, includeReactions: !!initialReactionEmojiToDisplay || false})
  }, [initialReactionEmojiToDisplay, loadIssueIndexLazyData, nodes, hasLazyData])

  const items = currentPageNodes?.map((node, index) => {
    if (node == null || node.id === deletedRecordId) {
      return null
    }

    return (
      <RowItem
        key={node.id}
        node={node}
        index={index}
        isSelected={checkedItems.has(node.id)}
        metadataRef={issueIndexLazyDataRef}
        getMetadataHref={getQueryFieldUrl}
        getAdditiveMetadataHref={getAdditiveQueryFieldUrl}
        onSelectRow={onSelectRow}
        onSidePanelNavigate={onSidePanelNavigate}
        reactionEmojiToDisplay={reactionEmojiToDisplay}
        sortingItemSelected={sortingItemSelected}
        scopedRepository={scoped_repository}
        navigate={navigate}
        itemSelectedRef={itemSelectedRef}
        handleNavigateRef={handleNavigateRef}
      />
    )
  })

  useEffect(() => {
    if (fromPagination && currentPage) {
      announce(LABELS.announcePage(currentPage, totalPages, items.length))
      setFromPagination(false)
    }
  }, [currentPage, fromPagination, items.length, totalPages])

  const showMoreResultsAvailableBanner =
    currentPage !== undefined && totalPages === currentPage && issueCount !== undefined && issueCount > maxItems

  const focusTabbableIssue = () => {
    if (listRef.current) {
      // either the first issue in the list or the last tabbed item have a tabindex of 0, while the rest have -1
      // this is the primer implementation of navigating the list with tabs / keys
      const tabbableItem = listRef.current.querySelector('[tabindex="0"]')
      if (tabbableItem && tabbableItem instanceof HTMLElement) {
        tabbableItem.focus()
      }
    }
  }

  const list = (
    <>
      <GlobalCommands
        commands={{
          'issues-react:focus-next-issue': focusTabbableIssue,
          'issues-react:focus-previous-issue': focusTabbableIssue,
        }}
      />
      <div
        data-testid="list-load-progress-bar"
        ref={paginationLoadingRef}
        className={clsx('turbo-progress-bar', styles.progressBar)}
      />
      {/* if we already have focus in the list, delegate keyboard navigation to ListView */}
      <ScopedCommands commands={NOOP_FOCUS_COMMANDS} className={styles.listScopedCommand}>
        <ListView
          {...testIdProps(TEST_IDS.list)}
          title={LABELS.searchResults}
          totalCount={issueCount || 0}
          selectedCount={checkedItems.size}
          titleHeaderTag="h2"
          isSelectable={useBulkActions}
          metadata={listItemsHeader}
          singularUnits={LABELS.singularUnits(currentPageNodeTypes)}
          pluralUnits={LABELS.pluralUnits(currentPageNodeTypes)}
          listRef={listRef}
          strictFocusZone={false}
        >
          {items}
          {items.length === 0 && searchResultsReady && <NoResults />}
          {showMoreResultsAvailableBanner && (
            <MoreResultsAvailableBanner itemsLabel={LABELS.pluralUnits(currentPageNodeTypes) ?? 'issues'} />
          )}
        </ListView>
      </ScopedCommands>
    </>
  )

  return (
    <div>
      <div data-hpc className={styles.listContainer}>
        {list}
      </div>
      {currentPage && totalPages > 1 ? (
        <Pagination
          pageCount={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          hrefBuilder={buildPageHref}
          marginPageCount={2}
          surroundingPageCount={2}
        />
      ) : null}
    </div>
  )
}

type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends ReadonlyArray<infer ElementType> ? ElementType : never

type NodeType = NonNullable<
  NonNullable<
    ArrayElement<NonNullable<Extract<ListItemsPaginated_results$data['search'], {ok: true}>['value']['edges']>>
  >['node']
>

type PullRequestNodeType = Extract<NodeType, {__typename: 'PullRequest'}>

export type IssueNodeType = Extract<NodeType, {__typename: 'Issue'}>
export type ItemNodeType = IssueNodeType | PullRequestNodeType
